import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from "react";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { IconWrapper } from "~/ui";
import type { Styled } from "~/utils";

export type ButtonVariant = "filled" | "text" | "outline";
export type ButtonSize = "sm" | "base" | "lg";
export type ButtonColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "neutral"
  | "error"
  | "success"
  | "info"
  | "warning";

const sizes = {
  sm: "py-2 px-6",
  base: "py-3 px-6",
  lg: "py-5 px-10",
};

const colors = {
  primary: {
    filled: "text-primary-20",
    outline: "dark:text-primary-80 text-primary-50",
    text: "dark:text-primary-80 text-primary-50",
  },
  secondary: {
    filled: "text-secondary-20",
    outline: "dark:text-secondary-80 text-secondary-50",
    text: "dark:text-secondary-80 text-secondary-50",
  },
  tertiary: {
    filled: "text-tertiary-20",
    outline: "dark:text-tertiary-80 text-tertiary-50",
    text: "dark:text-tertiary-80 text-tertiary-50",
  },
  neutral: {
    filled: "text-neutral-20",
    outline: "dark:text-neutral-80 text-neutral-50",
    text: "dark:text-neutral-80 text-neutral-50",
  },
  error: {
    filled: "text-error-20",
    outline: "dark:text-error-80 text-error-50",
    text: "dark:text-error-80 text-error-50",
  },
  success: {
    filled: "text-success-20",
    outline: "dark:text-success-80 text-success-50",
    text: "dark:text-success-80 text-success-50",
  },
  info: {
    filled: "text-info-20",
    outline: "dark:text-info-80 text-info-50",
    text: "dark:text-info-80 text-info-50",
  },
  warning: {
    filled: "text-warning-20",
    outline: "dark:text-warning-80 text-warning-50",
    text: "dark:text-warning-80 text-warning-50",
  },
  disabled: {
    filled: "text-neutral-variant-40 dark:text-neutral-variant-40",
    outline: "text-neutral-variant-40 dark:text-neutral-variant-40",
    text: "text-neutral-variant-40 dark:text-neutral-variant-40",
  },
};

const bgColors = {
  primary: {
    filled: "bg-primary-80",
    outline:
      "border border-primary-50 dark:border-primary-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  secondary: {
    filled: "bg-secondary-80",
    outline:
      "border border-secondary-50 dark:border-secondary-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  tertiary: {
    filled: "bg-tertiary-80",
    outline:
      "border border-tertiary-50 dark:border-tertiary-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  neutral: {
    filled: "bg-neutral-80",
    outline:
      "border border-neutral-50 dark:border-neutral-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  error: {
    filled: "bg-error-80",
    outline:
      "border border-error-50 dark:border-error-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  success: {
    filled: "bg-success-80",
    outline:
      "border border-success-50 dark:border-success-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  info: {
    filled: "bg-info-80",
    outline:
      "border border-info-50 dark:border-info-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  warning: {
    filled: "bg-warning-80",
    outline:
      "border border-warning-50 dark:border-warning-80 dark:hover:bg-tertiary-20 hover:bg-tertiary-90 active:bg-tertiary-20",
    text: "hover:bg-tertiary-20 active:bg-tertiary-20",
  },
  disabled: {
    filled: "bg-complementary-disabled",
    outline: "border border-complementary-disabled",
    text: "",
  },
};

export interface ButtonProps
  extends ComponentPropsWithoutRef<"button">,
    Styled {
  text: ReactNode;
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
}

export const Button = forwardRef(
  (
    {
      text,
      color = "primary",
      variant = "filled",
      size = "base",
      leftIcon,
      rightIcon,
      className,
      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const bSize = sizes[size];
    const bColor = bgColors[props.disabled ? "disabled" : color][variant];
    const fontColor = colors[props.disabled ? "disabled" : color][variant];

    return (
      <button
        type="button"
        className={twMerge(
          "flex-row items-center justify-center rounded-full",
          bColor,
          bSize,
          className,
        )}
        ref={ref}
        {...props}
      >
        {leftIcon && <IconWrapper>{leftIcon}</IconWrapper>}
        {text && (
          <span className={twMerge("text-base", fontColor)}>{text}</span>
        )}
        {rightIcon && <IconWrapper>{rightIcon}</IconWrapper>}
      </button>
    );
  },
);
Button.displayName = "Button";
